/** Optional Google measurement: no Google tag is loaded until a visitor opts in. */
import { isLocalAnalyticsHost } from "@/lib/analyticsHost";
import { currentPrivacyChoices } from "@/lib/privacyChoices";

export const GA_ID = process.env.NEXT_PUBLIC_GA_ID;
export const GOOGLE_ADS_ID = process.env.NEXT_PUBLIC_GOOGLE_ADS_ID || "AW-10812227182";
export const GOOGLE_ADS_SIGNUP_LABEL = process.env.NEXT_PUBLIC_GOOGLE_ADS_SIGNUP_LABEL || "o0_2COXn1K8dEO781aMo";
const GOOGLE_SCRIPT_ID = "novilot-google-analytics";

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    dataLayer?: unknown[];
    __novilotGoogleTagInitialized?: boolean;
    __novilotGaInitialized?: boolean;
    __novilotGaId?: string;
    __novilotAdsInitialized?: boolean;
  }
}

function usable(): boolean {
  return typeof window !== "undefined" && typeof document !== "undefined" &&
    !isLocalAnalyticsHost(window.location.hostname) &&
    !/^\/(?:editor-review|author-review|manuscript-request|review-invitation|read)\//.test(window.location.pathname ?? "");
}
function consentState() {
  const choices = currentPrivacyChoices();
  return { analytics_storage: choices.analytics ? "granted" : "denied", ad_storage: choices.advertising ? "granted" : "denied", ad_user_data: choices.advertising ? "granted" : "denied", ad_personalization: "denied" };
}
function ensureGoogleTag(id: string): void {
  if (window.__novilotGoogleTagInitialized) return;
  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || function gtag() {
    // gtag.js requires Arguments objects in its command queue.
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer?.push(arguments);
  };
  window.gtag("consent", "default", { analytics_storage: "denied", ad_storage: "denied", ad_user_data: "denied", ad_personalization: "denied" });
  window.gtag("consent", "update", consentState());
  window.gtag("js", new Date());
  if (!document.getElementById(GOOGLE_SCRIPT_ID)) {
    const script = document.createElement("script"); script.id = GOOGLE_SCRIPT_ID; script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`;
    document.head.appendChild(script);
  }
  window.__novilotGoogleTagInitialized = true;
}

export function initializeGoogleAnalytics(measurementId = GA_ID): void {
  if (!measurementId || !usable() || !currentPrivacyChoices().analytics || window.__novilotGaInitialized) return;
  ensureGoogleTag(measurementId);
  window.gtag?.("config", measurementId, { send_page_view: false, allow_google_signals: false, allow_ad_personalization_signals: false });
  window.__novilotGaId = measurementId;
  window.__novilotGaInitialized = true;
}
function productionAdsHost(): boolean {
  return typeof window !== "undefined" && ["novilot.com", "www.novilot.com"].includes(window.location.hostname.toLowerCase());
}
export function initializeGoogleAds(): void {
  if (!productionAdsHost() || !usable() || !currentPrivacyChoices().advertising || window.__novilotAdsInitialized || !/^AW-\d+$/.test(GOOGLE_ADS_ID)) return;
  ensureGoogleTag(GOOGLE_ADS_ID);
  window.gtag?.("config", GOOGLE_ADS_ID, { allow_ad_personalization_signals: false });
  window.__novilotAdsInitialized = true;
}
/** Apply a grant or withdrawal before any subsequent measurement event. */
export function updateGoogleConsent(): void {
  if (typeof window === "undefined" || typeof document === "undefined" || isLocalAnalyticsHost(window.location.hostname)) return;
  if (!usable()) {
    // A tag loaded on an earlier SPA page must be denied on a private route too.
    // Do not pass a bearer-token path as the tag's current page location.
    if (window.gtag) {
      window.gtag("set", "page_location", `${window.location.origin || `https://${window.location.hostname}`}/`);
      window.gtag("consent", "update", { analytics_storage: "denied", ad_storage: "denied", ad_user_data: "denied", ad_personalization: "denied" });
    }
    return;
  }
  if (window.gtag) window.gtag("consent", "update", consentState());
  initializeGoogleAnalytics();
  initializeGoogleAds();
}
export function trackGaEvent(event: string, params: Record<string, unknown> = {}): void {
  if (!usable() || !currentPrivacyChoices().analytics || !window.__novilotGaInitialized || !window.__novilotGaId || !window.gtag) return;
  window.gtag("event", event, { ...params, send_to: window.__novilotGaId });
}
/** Count only a server-confirmed new account, once per opaque transaction ID. */
export function trackAdsRegistration(transactionId: string): void {
  if (!productionAdsHost() || !usable() || !currentPrivacyChoices().advertising || !/^[a-f0-9]{64}$/.test(transactionId) || !/^[A-Za-z0-9_-]+$/.test(GOOGLE_ADS_SIGNUP_LABEL)) return;
  initializeGoogleAds();
  if (window.__novilotAdsInitialized) window.gtag?.("event", "conversion", { send_to: `${GOOGLE_ADS_ID}/${GOOGLE_ADS_SIGNUP_LABEL}`, transaction_id: transactionId });
}
